import type { ItemServiceType, LaundryItem, OrderPriority } from '@/types';

/** A line that can be priced (recorded order item or review line). */
export interface PricedLine {
  itemId: string;
  serviceType: ItemServiceType;
  quantity: number;
}

/** Unit price for one item + service + priority, or null when unavailable. */
export function unitPrice(
  item: LaundryItem | undefined,
  serviceType: ItemServiceType,
  priority: OrderPriority,
): number | null {
  const rate = item?.services?.[serviceType];
  if (!rate || !rate.available) return null;
  const value = priority === 'urgent' ? rate.urgent : rate.normal;
  return value ?? null;
}

export interface LinesTotal {
  total: number;
  /** True when at least one line has no approved price (service unavailable). */
  unavailable: boolean;
}

/**
 * Sum the approved prices of a set of lines. Lines whose service is not
 * available for the item are flagged instead of silently priced as zero.
 */
export function computeLinesTotal(
  lines: PricedLine[],
  priority: OrderPriority,
  itemsById: Map<string, LaundryItem>,
): LinesTotal {
  let total = 0;
  let unavailable = false;
  lines.forEach((line) => {
    const unit = unitPrice(itemsById.get(line.itemId), line.serviceType, priority);
    if (unit == null) {
      unavailable = true;
      return;
    }
    total += unit * line.quantity;
  });
  return { total: round2(total), unavailable };
}

export function round2(value: number): number {
  return Math.round(value * 100) / 100;
}